<script setup lang="ts">
import { ref, computed } from 'vue'
import { GoogleMap, Marker } from 'vue3-google-map'

const props = defineProps({
  position: {
    type: Object as () => { lat: number; lng: number },
    required: true,
  },
  zoom: {
    type: Number,
    default: 12,
  },
  markerOptions: {
    type: Object,
    default: () => ({}),
  },
  clickToLoad: {
    type: Boolean,
    default: false,
  },
})

const apiKey = window.Laravel.gmapApiKey
const isInteractive = ref(false)

const staticMapUrl = computed(() => {
  const params = new URLSearchParams({
    center: `${props.position.lat},${props.position.lng}`,
    zoom: String(props.zoom),
    size: '640x320',
    scale: '2',
    markers: `color:red|${props.position.lat},${props.position.lng}`,
    key: apiKey,
  })
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`
})

const activate = () => {
  if (props.clickToLoad) {
    isInteractive.value = true
  }
}
</script>

<template>
  <div class="map-with-single-marker">
    <GoogleMap
      v-if="isInteractive"
      :api-key="apiKey"
      :center="position"
      :zoom="zoom"
      style="width: 100%; height: 100%">
      <Marker :options="{ ...markerOptions, position }" />
    </GoogleMap>
    <div
      v-else
      class="static-map"
      :class="{ 'static-map--clickable': clickToLoad }"
      :style="{ backgroundImage: `url('${staticMapUrl}')` }"
      :role="clickToLoad ? 'button' : 'img'"
      :tabindex="clickToLoad ? 0 : -1"
      @click="activate"
      @keydown.enter="activate">
      <span v-if="clickToLoad" class="static-map__overlay">
        <i class="fa fa-hand-pointer-o mr-2"></i>
        {{ $t('Click to interact with the map') }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.map-with-single-marker {
  width: 100%;
  height: 100%;
  position: relative;
}

.static-map {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 0.75rem;
}

.static-map--clickable {
  cursor: pointer;
  transition: filter 0.2s ease;
}

.static-map--clickable:hover,
.static-map--clickable:focus {
  filter: brightness(0.95);
  outline: none;
}

.static-map__overlay {
  background: rgba(255, 255, 255, 0.95);
  padding: 0.4rem 0.9rem;
  border-radius: 0.25rem;
  font-size: 0.85rem;
  font-weight: 500;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  color: #333;
}
</style>
